import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  FiPlus,
  FiSearch,
  FiUpload,
  FiUsers,
  FiFilter,
  FiCalendar,
  FiFileText,
  FiShield,
  FiUserCheck,
  FiCheckCircle,
  FiXCircle,
  FiChevronDown,
  FiLink,
  FiPaperclip,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import AddApplicant from './AddApplicant';
import ShareApplyLink from './ShareApplyLink';
import HireEmployeeModal from './HireEmployeeModal';
import { useAdminPageSearch } from '../../../utils/useAdminPageSearch';
import {
  APPLICANTS_QUERY,
  UPDATE_APPLICANT_STAGE,
  RECRUITMENT_STAGES,
  applicantInitials,
  priorityLabel,
  mapApplicant,
  isCreatedToday,
} from '../data/recruitmentApi';
import '../styles/RecruitmentManagement.css';

const VIEW_TABS = [
  { id: 'all', label: 'All Applicants', page: 'recruitment-applicants' },
  { id: 'interview', label: 'Interviews', page: 'recruitment-interviews' },
  { id: 'offer', label: 'Job Offers', page: 'recruitment-offers' },
  {
    id: 'background',
    label: 'Background Checks',
    page: 'recruitment-background',
  },
  { id: 'onboarding', label: 'Onboarding' },
];

const STAT_DEFS = [
  { id: 'total', label: 'Total Applicants', icon: FiUsers, tone: 'blue' },
  { id: 'screening', label: 'Screening', icon: FiFilter, tone: 'purple' },
  { id: 'interview', label: 'Interviews', icon: FiCalendar, tone: 'yellow' },
  { id: 'offer', label: 'Offers Sent', icon: FiFileText, tone: 'teal' },
  { id: 'background', label: 'Background Checks', icon: FiShield, tone: 'orange' },
  { id: 'onboarding', label: 'Onboarding', icon: FiUserCheck, tone: 'green' },
  { id: 'hired', label: 'Hired', icon: FiCheckCircle, tone: 'lime' },
  { id: 'rejected', label: 'Rejected', icon: FiXCircle, tone: 'red' },
];

function moveCandidate(list, source, destination) {
  const next = list.map((c) => ({ ...c }));
  const fromItems = next.filter((c) => c.stage === source.droppableId);
  const moving = fromItems[source.index];
  if (!moving) return list;

  const without = next.filter((c) => c.id !== moving.id);
  const destItems = without.filter((c) => c.stage === destination.droppableId);
  const updated = { ...moving, stage: destination.droppableId };
  const insertAt =
    destination.index >= destItems.length
      ? without.length
      : (() => {
          const target = destItems[destination.index];
          return without.findIndex((c) => c.id === target.id);
        })();

  without.splice(insertAt < 0 ? without.length : insertAt, 0, updated);
  return without;
}

export default function RecruitmentManagement() {
  const navigate = useNavigate();
  const { get, patch } = useAdminPageSearch();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const urlTab = get('tab');
  const activeTab = urlTab === 'onboarding' ? 'onboarding' : 'pipeline';
  const showAdd = get('action') === 'add';
  const hireId = get('hire');
  const [search, setSearch] = useState('');
  const [jobPosition, setJobPosition] = useState('');
  const [branch, setBranch] = useState('');
  const [recruiter, setRecruiter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showShareLink, setShowShareLink] = useState(false);
  const hireApplicant = useMemo(
    () => candidates.find((c) => String(c.id) === String(hireId)) || null,
    [candidates, hireId],
  );

  const fetchApplicants = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await graphqlRequest(APPLICANTS_QUERY);
      const rows = Array.isArray(data?.recruitmentApplicants)
        ? data.recruitmentApplicants
        : [];
      setCandidates(rows.map(mapApplicant));
    } catch (err) {
      console.error(err);
      setCandidates([]);
      setError(err?.message || 'Failed to load applicants.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchApplicants();
  }, [fetchApplicants]);

  const jobOptions = useMemo(
    () => [...new Set(candidates.map((c) => c.role).filter(Boolean))].sort(),
    [candidates],
  );
  const branchOptions = useMemo(
    () => [...new Set(candidates.map((c) => c.branch).filter(Boolean))].sort(),
    [candidates],
  );
  const recruiterOptions = useMemo(
    () =>
      [...new Set(candidates.map((c) => c.recruiter).filter(Boolean))].sort(),
    [candidates],
  );

  const goPage = (page) => {
    navigate({ pathname: '/', search: `?page=${page}` }, { replace: true });
  };

  const handleTabClick = (tab) => {
    if (tab.page) {
      goPage(tab.page);
      return;
    }
    patch(
      { tab: tab.id === 'onboarding' ? 'onboarding' : '' },
      ['hire', 'action'],
    );
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = candidates.filter((c) => {
      if (
        activeTab !== 'pipeline' &&
        activeTab !== 'all' &&
        c.stage !== activeTab
      ) {
        return false;
      }
      if (jobPosition && c.role !== jobPosition) return false;
      if (branch && c.branch !== branch) return false;
      if (recruiter && c.recruiter !== recruiter) return false;
      if (stageFilter && c.stage !== stageFilter) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        c.role.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.phone.toLowerCase().includes(q)
      );
    });

    if (sortBy === 'name') {
      list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'priority') {
      const rank = { high: 0, normal: 1, low: 2 };
      list = [...list].sort(
        (a, b) => (rank[a.priority] ?? 9) - (rank[b.priority] ?? 9),
      );
    }
    return list;
  }, [
    candidates,
    activeTab,
    search,
    jobPosition,
    branch,
    recruiter,
    stageFilter,
    sortBy,
  ]);

  const columns = useMemo(() => {
    const visibleStages =
      activeTab === 'pipeline' || activeTab === 'all'
        ? RECRUITMENT_STAGES
        : RECRUITMENT_STAGES.filter((s) => s.id === activeTab);
    return visibleStages.map((stage) => ({
      ...stage,
      items: filtered.filter((c) => c.stage === stage.id),
    }));
  }, [filtered, activeTab]);

  const stats = useMemo(() => {
    const todayByStage = (stage) =>
      candidates.filter(
        (c) => c.stage === stage && isCreatedToday(c.createdAt),
      ).length;
    const counts = {
      total: candidates.length,
      screening: candidates.filter((c) => c.stage === 'screening').length,
      interview: candidates.filter((c) => c.stage === 'interview').length,
      offer: candidates.filter((c) => c.stage === 'offer').length,
      background: candidates.filter((c) => c.stage === 'background').length,
      onboarding: candidates.filter((c) => c.stage === 'onboarding').length,
      hired: candidates.filter((c) => c.stage === 'hired').length,
      rejected: candidates.filter((c) => c.stage === 'rejected').length,
    };
    const today = {
      total: candidates.filter((c) => isCreatedToday(c.createdAt)).length,
      screening: todayByStage('screening'),
      interview: todayByStage('interview'),
      offer: todayByStage('offer'),
      background: todayByStage('background'),
      onboarding: todayByStage('onboarding'),
      hired: todayByStage('hired'),
      rejected: todayByStage('rejected'),
    };
    return STAT_DEFS.map((s) => {
      const t = today[s.id] || 0;
      return {
        ...s,
        value: counts[s.id] ?? 0,
        today: t > 0 ? `+${t} today` : '0 today',
        todayTone: t > 0 ? 'up' : 'neutral',
      };
    });
  }, [candidates]);

  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const fromItems = candidates.filter((c) => c.stage === source.droppableId);
    const moving = fromItems[source.index];
    if (!moving) return;

    // Moving to Hired only updates the pipeline stage.
    // Creating the employee portal account is a separate "Hire & Invite" action
    // so already-hired candidates can be moved back without re-sending email.

    const previous = candidates;
    setCandidates((prev) => moveCandidate(prev, source, destination));

    try {
      await graphqlRequest(UPDATE_APPLICANT_STAGE, {
        id: moving.id,
        stage: destination.droppableId,
      });
    } catch (err) {
      setCandidates(previous);
      toast.error(err?.message || 'Could not update applicant stage.');
    }
  };

  return (
    <div className="rm-page">
      <header className="rm-header">
        <div>
          <h1 className="rm-title">Recruitment Management</h1>
          <p className="rm-subtitle">
            Manage candidates from application to onboarding.
          </p>
        </div>
        <div className="rm-header-actions">
          <button type="button" className="rm-btn rm-btn--ghost">
            <FiUpload />
            Import Resume
          </button>
          <button
            type="button"
            className="rm-btn rm-btn--ghost"
            onClick={() => setShowShareLink(true)}
          >
            <FiLink />
            Share Apply Link
          </button>
          <button
            type="button"
            className="rm-btn rm-btn--primary"
            onClick={() => patch({ action: 'add' }, ['hire'])}
          >
            <FiPlus />
            Add Applicant
          </button>
        </div>
      </header>

      {error ? (
        <div className="rm-error">
          <span>{error}</span>
          <button type="button" onClick={() => void fetchApplicants()}>
            Retry
          </button>
        </div>
      ) : null}

      <section className="rm-stats" aria-label="Recruitment statistics">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <article key={stat.id} className="rm-stat-card">
              <div className={`rm-stat-icon rm-stat-icon--${stat.tone}`}>
                <Icon />
              </div>
              <div className="rm-stat-body">
                <div className="rm-stat-value">{stat.value}</div>
                <div className="rm-stat-label">{stat.label}</div>
                <div className={`rm-stat-today rm-stat-today--${stat.todayTone}`}>
                  {stat.today}
                </div>
              </div>
            </article>
          );
        })}
      </section>

      <section className="rm-toolbar">
        <div className="rm-tabs" role="tablist">
          {VIEW_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`rm-tab${activeTab === tab.id ? ' is-active' : ''}`}
              onClick={() => handleTabClick(tab)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="rm-filters">
          <label className="rm-search">
            <FiSearch />
            <input
              type="search"
              placeholder="Search applicants, positions, email, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>

          <div className="rm-select-wrap">
            <select
              value={jobPosition}
              onChange={(e) => setJobPosition(e.target.value)}
              aria-label="Job Position"
            >
              <option value="">Job Position</option>
              {jobOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <FiChevronDown />
          </div>

          <div className="rm-select-wrap">
            <select
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              aria-label="Branch"
            >
              <option value="">Branch</option>
              {branchOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <FiChevronDown />
          </div>

          <div className="rm-select-wrap">
            <select
              value={recruiter}
              onChange={(e) => setRecruiter(e.target.value)}
              aria-label="Recruiter"
            >
              <option value="">Recruiter</option>
              {recruiterOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <FiChevronDown />
          </div>

          <div className="rm-select-wrap">
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              aria-label="Stage"
            >
              <option value="">Stage</option>
              {RECRUITMENT_STAGES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </select>
            <FiChevronDown />
          </div>

          <button type="button" className="rm-more-btn">
            More
            <FiChevronDown />
          </button>

          <div className="rm-select-wrap rm-select-wrap--sort">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              aria-label="Sort"
            >
              <option value="newest">Newest</option>
              <option value="name">Name</option>
              <option value="priority">Priority</option>
            </select>
            <FiChevronDown />
          </div>
        </div>
      </section>

      <section className="rm-pipeline">
        <h2 className="rm-pipeline-title">Recruitment Pipeline</h2>

        {loading ? (
          <div className="rm-empty-state">Loading applicants…</div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="rm-board">
              {columns.map((col) => (
                <div key={col.id} className="rm-column">
                  <div className="rm-column-header">
                    <span
                      className="rm-column-dot"
                      style={{ background: col.color }}
                    />
                    <span className="rm-column-name">{col.title}</span>
                    <span className="rm-column-count">{col.items.length}</span>
                  </div>

                  <Droppable droppableId={col.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`rm-column-body${
                          snapshot.isDraggingOver ? ' is-over' : ''
                        }`}
                      >
                        {col.items.map((item, index) => (
                          <Draggable
                            key={item.id}
                            draggableId={item.id}
                            index={index}
                          >
                            {(dragProvided, dragSnapshot) => (
                              <article
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                className={`rm-card${
                                  dragSnapshot.isDragging ? ' is-dragging' : ''
                                }`}
                              >
                                <div className="rm-card-top">
                                  <div
                                    className="rm-avatar"
                                    style={{ background: item.avatar }}
                                  >
                                    {applicantInitials(item.name)}
                                  </div>
                                  <span
                                    className={`rm-priority rm-priority--${item.priority}`}
                                  >
                                    {priorityLabel(item.priority)}
                                  </span>
                                </div>
                                <h3 className="rm-card-name">{item.name}</h3>
                                <p className="rm-card-role">{item.role}</p>
                                <div className="rm-card-meta">
                                  <span>{item.experience}</span>
                                  <span>{item.date}</span>
                                </div>
                                {item.resumeFileUrl ? (
                                  <a
                                    className="rm-card-resume"
                                    href={item.resumeFileUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <FiPaperclip />
                                    {item.resumeFileName || 'Resume'}
                                  </a>
                                ) : null}
                                {item.stage === 'onboarding' ? (
                                  <button
                                    type="button"
                                    className="rm-card-hire"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      patch({ hire: item.id }, ['action']);
                                    }}
                                  >
                                    Hire &amp; Invite
                                  </button>
                                ) : null}
                              </article>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        <button
                          type="button"
                          className="rm-add-card"
                          onClick={() => patch({ action: 'add' }, ['hire'])}
                        >
                          <FiPlus />
                          Add
                        </button>
                      </div>
                    )}
                  </Droppable>
                </div>
              ))}
            </div>
          </DragDropContext>
        )}
      </section>

      {showAdd ? (
        <AddApplicant
          onClose={() => patch({}, ['action'])}
          onCreated={() => void fetchApplicants()}
        />
      ) : null}

      {showShareLink ? (
        <ShareApplyLink onClose={() => setShowShareLink(false)} />
      ) : null}

      {hireApplicant ? (
        <HireEmployeeModal
          applicant={hireApplicant}
          onClose={() => patch({}, ['hire'])}
          onHired={async () => {
            const hiredId = hireApplicant.id;
            try {
              await graphqlRequest(UPDATE_APPLICANT_STAGE, {
                id: hiredId,
                stage: 'hired',
              });
              patch({}, ['hire']);
              await fetchApplicants();
            } catch (err) {
              toast.error(
                err?.message ||
                  'Employee was created, but applicant stage could not be updated.',
              );
              patch({}, ['hire']);
              await fetchApplicants();
            }
          }}
        />
      ) : null}
    </div>
  );
}
